import {
  addMember,
  fetchMembers,
  fetchShareLink,
  mintShareLink,
  removeMember,
  revokeShareLink,
  updateMemberRole,
  useSDK,
  type AddMemberInput,
  type LittlebirdSDK,
  type ProjectMemberRole,
} from '@littlebird/sdk';
import {
  mapShareError,
  useProjectShareLink,
  type ProjectShareLinkAdapter,
} from '@littlebird/ui-utils';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { toast } from 'sonner';

import { queryClient } from '@/query/client';

const membersKey = (projectId: string) =>
  ['project-members', projectId] as const;

export const invalidateProjectMembers = (projectId: string) => {
  queryClient.invalidateQueries({ queryKey: membersKey(projectId) });
};

/**
 * `enabled` is not optional in practice: every sidebar row mounts a ShareProjectModal,
 * so an always-on query means one members request per project on app open.
 */
export function useProjectMembers(projectId: string, enabled = true) {
  const sdk = useSDK();
  return useQuery({
    queryKey: membersKey(projectId),
    queryFn: () => fetchMembers(sdk, projectId),
    enabled,
  });
}

function useMembersMutation<TArgs>(
  projectId: string,
  run: (sdk: LittlebirdSDK, args: TArgs) => Promise<unknown>
) {
  const sdk = useSDK();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (args: TArgs) => run(sdk, args),
    onSuccess: () =>
      client.invalidateQueries({ queryKey: membersKey(projectId) }),
  });
}

export const useAddMember = (projectId: string) =>
  useMembersMutation(projectId, (sdk, input: AddMemberInput) =>
    addMember(sdk, projectId, input)
  );

export const useUpdateMemberRole = (projectId: string) =>
  useMembersMutation(
    projectId,
    (sdk, args: { membershipId: string; role: ProjectMemberRole }) =>
      updateMemberRole(sdk, projectId, args.membershipId, args.role)
  );

export const useRemoveMember = (projectId: string) =>
  useMembersMutation(projectId, (sdk, membershipId: string) =>
    removeMember(sdk, projectId, membershipId)
  );

export function useShareLink(
  projectId: string,
  isOpen: boolean,
  canManageLink: boolean
) {
  const sdk = useSDK();
  const adapter = useMemo<ProjectShareLinkAdapter>(
    () => ({
      fetchToken: () => fetchShareLink(sdk, projectId),
      mintToken: () => mintShareLink(sdk, projectId),
      revokeToken: () => revokeShareLink(sdk, projectId),
      refreshCatalog: () => sdk.stores.projects.refreshProjects(),
      onError: (error, action) => {
        console.error(`Failed to ${action} share link:`, error);
        if (action === 'mint' || action === 'revoke') {
          toast.error(mapShareError(error));
        }
      },
    }),
    [projectId, sdk]
  );
  return useProjectShareLink({ isOpen, canManageLink, adapter });
}
